import { type OrderLineDiscountRecord, OrderLineDiscountSourceLabels } from '@navio/nsdk';
import { formatDiscountAmount } from 'noddi-util';

import { MembershipProgramLogo } from '../Membership/MembershipProgramLogo';
import { DiscountLineActions } from './DiscountLineActions';

type DiscountLinesProps = {
  discounts: OrderLineDiscountRecord[];
  orderLineFrozenAt?: string | null;
  /** When provided, renders partner-only edit/delete controls per discount and calls this on success. */
  onDiscountMutated?: () => void;
  /** Partner surfaces: name what put each discount on the line (coupon, membership, ...). */
  showSource?: boolean;
  getImageUrl?: (discountName: string) => string | undefined;
};

/**
 * Discount rows under an order line. Names start where the line title starts and amounts end where the
 * line price ends; the line's own price column carries what is owed after them (`OrderLinePrice`).
 */
export const DiscountLines = ({
  discounts,
  orderLineFrozenAt,
  onDiscountMutated,
  showSource,
  getImageUrl
}: DiscountLinesProps) => {
  return (
    <div className='flex flex-col gap-0.5'>
      {discounts.map((discount) => {
        const imageUrl = getImageUrl?.(discount.name);
        return (
          <div key={discount.id} className='flex items-center gap-2 text-sm text-foreground'>
            <span className='min-w-0 truncate'>{discount.name}</span>
            {/* After the name, so every name starts on the same edge whether or not it has a logo. */}
            {imageUrl && <MembershipProgramLogo url={imageUrl} size='small' />}
            {showSource && (
              <span className='shrink-0 text-xs text-muted-foreground'>
                {OrderLineDiscountSourceLabels[discount.source]}
              </span>
            )}
            <span className='ml-auto shrink-0 text-successDark'>{formatDiscountAmount(discount.amountGross)}</span>
            {onDiscountMutated && (
              <DiscountLineActions
                discount={discount}
                orderLineFrozenAt={orderLineFrozenAt}
                onSuccess={onDiscountMutated}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
